import { aspectValue, type Comic } from '@panels/shared';
import { comicFilename } from './filename.js';
import { renderOpLog } from './render.js';

/**
 * Comic strip rendering for download and clipboard (§9). Same stroke-to-bitmap
 * path as everything else — the exported PNG is the drawing, re-rendered from the
 * op log at 2x, not a screenshot of anything.
 */

export interface StripOptions {
  /** Panel width in layout pixels before the scale factor. */
  panelWidth?: number;
  scale?: number;
  padding?: number;
  gap?: number;
  borderWidth?: number;
  showTitle?: boolean;
}

const DEFAULTS: Required<StripOptions> = {
  panelWidth: 420,
  scale: 2,
  padding: 24,
  gap: 16,
  borderWidth: 2,
  showTitle: true,
};

export function renderComicStrip(comic: Comic, options: StripOptions = {}): HTMLCanvasElement {
  const { panelWidth, scale, padding, gap, borderWidth, showTitle } = { ...DEFAULTS, ...options };

  const ratio = aspectValue(comic.aspectRatio);
  const panelHeight = panelWidth / ratio;
  const panels = [...comic.panels].sort((a, b) => a.position - b.position);
  const count = Math.max(1, panels.length);

  const title = showTitle && comic.title ? comic.title : null;
  const titleHeight = title ? 46 : 0;

  const layoutWidth = padding * 2 + panelWidth * count + gap * (count - 1);
  const layoutHeight = padding * 2 + titleHeight + panelHeight;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(layoutWidth * scale);
  canvas.height = Math.round(layoutHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, layoutWidth, layoutHeight);

  if (title) {
    ctx.fillStyle = '#111111';
    ctx.font = '600 24px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(title, layoutWidth / 2, padding + titleHeight / 2 - 4, layoutWidth - padding * 2);
  }

  const top = padding + titleHeight;

  panels.forEach((panel, index) => {
    const left = padding + index * (panelWidth + gap);

    // Each panel is rendered on its own then composited, so a panel's strokes can
    // never bleed into its neighbour.
    const panelCanvas = document.createElement('canvas');
    panelCanvas.width = Math.round(panelWidth * scale);
    panelCanvas.height = Math.round(panelHeight * scale);
    const panelCtx = panelCanvas.getContext('2d');
    if (panelCtx) {
      panelCtx.setTransform(scale, 0, 0, scale, 0, 0);
      renderOpLog(panelCtx, panel.ops, panelWidth, panelHeight);
    }
    ctx.drawImage(panelCanvas, left, top, panelWidth, panelHeight);

    ctx.strokeStyle = '#111111';
    ctx.lineWidth = borderWidth;
    ctx.strokeRect(left + borderWidth / 2, top + borderWidth / 2, panelWidth - borderWidth, panelHeight - borderWidth);

    // An unfinished panel gets an honest label rather than a blank box.
    if (!panel.ops && panel.status !== 'submitted') {
      ctx.save();
      ctx.fillStyle = '#9aa0a6';
      ctx.font = '500 16px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('unfinished', left + panelWidth / 2, top + panelHeight / 2);
      ctx.restore();
    }
  });

  return canvas;
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

export async function downloadComicPng(comic: Comic, options?: StripOptions): Promise<void> {
  const blob = await canvasToBlob(renderComicStrip(comic, options));
  if (!blob) throw new Error('Could not render that comic');

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = comicFilename(comic);
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the download a beat to start before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export type CopyResult = 'copied' | 'unsupported' | 'failed';

export async function copyComicToClipboard(comic: Comic, options?: StripOptions): Promise<CopyResult> {
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) return 'unsupported';
  try {
    const blob = await canvasToBlob(renderComicStrip(comic, options));
    if (!blob) return 'failed';
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    return 'copied';
  } catch {
    return 'failed';
  }
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older iOS Safari and any non-secure context land here.
    try {
      const area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      area.remove();
      return ok;
    } catch {
      return false;
    }
  }
}
